import chalk from 'chalk';
import {
  EVENTS,
  type IConfig,
  type IMutableContext,
  type IToggle,
  UnleashClient,
} from 'unleash-proxy-client';

import { type CustomEvent, CustomEventEmitter } from '@/util/event';

import type { UnleashPayload } from '../type';

export class Unleash extends CustomEventEmitter<{
  initialized: CustomEvent<'initialized'>;
  ready: CustomEvent<'ready'>;
  recovered: CustomEvent<'recovered'>;
  sent: CustomEvent<
    'sent',
    null,
    {
      appName: string;
      bucket: { start: Date; stop: Date; toggles: IToggle[] };
      instanceId: string;
    }
  >;
  update: CustomEvent<'update'>;
}> {
  static instance: Nullish<Unleash>;
  private static retryCount = 0;

  private _client!: UnleashClient;
  private _isError: boolean = false;
  private _isLoading: boolean = false;

  constructor(
    public config: IConfig,
    public fallbackToggleList: IToggle[] = [],
  ) {
    if (Unleash.instance instanceof Unleash) {
      return Unleash.instance;
    }

    super();

    this._isLoading = true;
    this._client = new UnleashClient(config);

    Object.values(EVENTS).forEach(eventName => {
      this._client.on(eventName, (payload: any) => {
        /* v8 ignore start */
        this.emit(eventName as any, payload);
        /* v8 ignore end */
      });
    });

    this._client.on(EVENTS.ERROR, this._onError.bind(this));
    this._client.on(EVENTS.READY, this._onReady.bind(this));

    Unleash.instance = this;
  }

  private _assert() {
    return this._client != null && this.isReady() && !this._isError;
  }

  private _onError(event: any) {
    this._isError = true;
    this._isLoading = false;

    /* v8 ignore start */
    console.group('[Unleash Error]');
    console.warn(event);
    console.groupEnd();
    /* v8 ignore end */

    if (!this._isLoading || Unleash.retryCount < 10) {
      Unleash.retryCount++;

      setTimeout(() => this.changeConfig(this.config), 100);
    }
  }

  private _onReady() {
    this._isError = false;
    this._isLoading = false;
    Unleash.retryCount = 0;

    /* v8 ignore start */
    console.log(
      chalk.bgBlack(chalk.white(`[unleash] appName: '${this.config.appName}'`)),
    );
    /* v8 ignore end */
  }

  get client() {
    return this._client;
  }

  changeConfig(config: Partial<IConfig>) {
    Unleash.instance = null;

    return createUnleashClient({
      ...this.config,
      ...config,
    });
  }

  getAllToggles() {
    const toggleList = this._client.getAllToggles();

    return this._assert()
      ? toggleList.length > 0
        ? toggleList
        : this.fallbackToggleList
      : this.fallbackToggleList;
  }

  getFlagList() {
    return this.getAllToggles().map(v => v.name);
  }

  getVariant(toggleName: string) {
    return this._assert()
      ? this._client.getVariant(toggleName)
      : this.fallbackToggleList.find(v => v.name === toggleName)?.variant;
  }

  getVariantPayloadValue<T = any>(toggleName: string): Nullish<T> {
    const rawPayload = this.getVariant(toggleName)?.payload;

    if (rawPayload == null) return null as any;

    const { type, value } = rawPayload as UnleashPayload;

    switch (type) {
      case 'string':
      case 'csv':
        return value as any;
      case 'number':
      case 'json':
        return JSON.parse(value) as any;
      default:
        return value as any;
    }
  }

  isEnabled(toggleName: string) {
    return this._client.isEnabled(toggleName);
  }

  isReady() {
    return this._client.isReady();
  }

  start() {
    if (this._client.isReady()) {
      console.warn('Unleash has already started 🫢');
      return Promise.resolve();
    }

    return this._client.start();
  }

  stop() {
    return this._client.stop();
  }

  updateContext(context: IMutableContext) {
    if (!this._assert()) return;

    return this._client.updateContext(context);
  }
}

const createUnleashClient = (
  config: IConfig,
  fallbackToggleList?: IToggle[],
) => {
  const unleash = new Unleash(config, fallbackToggleList);

  return unleash;
};

export default createUnleashClient;
